import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  createPayrollChangeset,
  getPayrollEmployee,
  fetchPayrollEssDashboard,
  submitPayrollPtoRequest,
  acknowledgePayrollPolicy,
} from '@/lib/api/payrollBridge';
import type { PayrollEmployeeRecord } from '@/lib/api/payrollBridge';

interface AddressFormState {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
}

const normaliseAddress = (address: PayrollEmployeeRecord['address']): AddressFormState => ({
  line1: typeof address?.line1 === 'string' ? address.line1 : '',
  line2: typeof address?.line2 === 'string' ? address.line2 : '',
  city: typeof address?.city === 'string' ? address.city : '',
  state: typeof address?.state === 'string' ? address.state : '',
  postalCode: typeof address?.postalCode === 'string' ? address.postalCode : '',
});

const statusClassName = (status: string): string => {
  switch (status) {
    case 'completed':
      return 'border-emerald-200 bg-emerald-100 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100';
    case 'blocked':
      return 'border-amber-200 bg-amber-100 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100';
    case 'in_progress':
      return 'border-sky-200 bg-sky-100 text-sky-800 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-100';
    default:
      return 'border-border/60 bg-muted/30 text-muted-foreground dark:border-border/50 dark:bg-muted/10 dark:text-muted-foreground';
  }
};

const PTO_STATUS_STYLES: Record<'pending' | 'approved' | 'denied', string> = {
  pending:
    'border-amber-200 bg-amber-100 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100',
  approved:
    'border-emerald-200 bg-emerald-100 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100',
  denied:
    'border-rose-200 bg-rose-100 text-rose-900 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-100',
};

const ESS_ALERT_STYLES: Record<'info' | 'warning', string> = {
  info: 'border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-100',
  warning:
    'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100',
};

const shiftFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
});

const currencyFormatter = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
});

const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: '2-digit',
});

export const PayrollEmployeePortal = ({ employeeId }: { employeeId: string }) => {
  const { t } = useTranslation(['common', 'payrollBridge']);
  const { toast } = useToast();

  const employeeQuery = useQuery({
    queryKey: ['payroll-employee', employeeId],
    queryFn: () => getPayrollEmployee(employeeId),
  });
  const employee = employeeQuery.data?.employee ?? null;

  const dashboardQuery = useQuery({
    queryKey: ['payroll-ess-dashboard', employeeId],
    queryFn: () => fetchPayrollEssDashboard(employeeId),
    enabled: Boolean(employeeId),
  });
  const dashboard = dashboardQuery.data ?? null;

  const [address, setAddress] = useState<AddressFormState>(() => normaliseAddress(employee?.address ?? null));
  const [submitted, setSubmitted] = useState(false);
  const [ptoForm, setPtoForm] = useState({
    policyId: '',
    hours: '4',
    startDate: '',
    endDate: '',
    notes: '',
  });
  const [ackForm, setAckForm] = useState({
    policyKey: '',
    policyTitle: '',
    version: '',
  });

  useEffect(() => {
    setAddress(normaliseAddress(employee?.address ?? null));
  }, [employee?.address]);

  useEffect(() => {
    if (!dashboard) {
      return;
    }
    if (!ptoForm.policyId && dashboard.accrualBalances.length > 0) {
      setPtoForm((prev) => ({
        ...prev,
        policyId: dashboard.accrualBalances[0].policyId,
      }));
    }
  }, [dashboard, ptoForm.policyId]);

  useEffect(() => {
    if (!dashboard) {
      return;
    }
    if (!ackForm.policyKey) {
      const doc = dashboard.documentAcknowledgements.find((candidate) => {
        const meta = candidate.metadata ?? {};
        return typeof (meta as Record<string, unknown>).policyKey === 'string';
      });
      const meta = (doc?.metadata ?? {}) as Record<string, unknown>;
      setAckForm({
        policyKey: typeof meta.policyKey === 'string' ? (meta.policyKey as string) : 'employee_handbook',
        policyTitle:
          typeof meta.policyTitle === 'string'
            ? (meta.policyTitle as string)
            : doc?.docType ?? t('payrollBridge:ess.documents'),
        version: typeof meta.version === 'string' ? (meta.version as string) : 'v1',
      });
    }
  }, [dashboard, ackForm.policyKey, t]);

  const changesetMutation = useMutation({
    mutationFn: createPayrollChangeset,
    onSuccess: () => {
      setSubmitted(true);
      void employeeQuery.refetch();
    },
  });

  const ptoMutation = useMutation({
    mutationFn: (payload: {
      policyId: string;
      hours: number;
      startDate: string;
      endDate?: string | null;
      notes?: string;
    }) => submitPayrollPtoRequest(employeeId, payload),
    onSuccess: () => {
      toast({
        title: t('common:status.success', { defaultValue: 'Success' }),
        description: t('payrollBridge:ess.requestPtoSuccess'),
      });
      setPtoForm({ policyId: '', hours: '4', startDate: '', endDate: '', notes: '' });
      void dashboardQuery.refetch();
    },
    onError: (error: unknown) => {
      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description:
          error instanceof Error
            ? error.message
            : t('common:status.tryAgain', { defaultValue: 'Please try again.' }),
      });
    },
  });

  const policyAckMutation = useMutation({
    mutationFn: (payload: { policyKey: string; policyTitle: string; version: string }) =>
      acknowledgePayrollPolicy(employeeId, {
        ...payload,
        acknowledgedAt: new Date().toISOString(),
      }),
    onSuccess: () => {
      toast({
        title: t('common:status.success', { defaultValue: 'Success' }),
        description: t('payrollBridge:ess.policyAckSuccess'),
      });
      void dashboardQuery.refetch();
    },
    onError: (error: unknown) => {
      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description:
          error instanceof Error
            ? error.message
            : t('common:status.tryAgain', { defaultValue: 'Please try again.' }),
      });
    },
  });

  const tasksData = employee?.tasks;
  const tasks = useMemo(() => tasksData ?? [], [tasksData]);
  const completedTasks = useMemo(
    () => tasks.filter((task) => task.status === 'completed'),
    [tasks],
  );

  const totalTasks = tasks.length;

  const upcomingShifts = dashboard?.upcomingShifts ?? [];
  const periodSummaries = dashboard?.periodSummaries ?? [];
  const accrualBalances = dashboard?.accrualBalances ?? [];
  const ptoRequests = dashboard?.ptoRequests ?? [];
  const essAlerts = dashboard?.alerts ?? [];
  const documents = dashboard?.documentAcknowledgements ?? [];

  const ptoHoursValue = Number.parseFloat(ptoForm.hours);
  const ptoSubmitDisabled =
    !ptoForm.policyId || !ptoForm.startDate || Number.isNaN(ptoHoursValue) || ptoHoursValue <= 0;
  const ackSubmitDisabled = !ackForm.policyKey || !ackForm.policyTitle || !ackForm.version;

  const renderTaskStatus = (status: string) =>
    t(`payrollBridge:onboardingTable.status.${status as 'pending'}`, {
      defaultValue: status,
    });

  const renderAssignment = (assignedTo: string | null | undefined) =>
    t(
      `payrollBridge:onboardingTable.assigned.${assignedTo === 'manager' ? 'manager' : 'employee'}`,
      {
        defaultValue: assignedTo === 'manager' ? 'Manager task' : 'Employee task',
      },
    );

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!employee) {
      return;
    }
    setSubmitted(false);
    changesetMutation.mutate({
      employeeId: employee.id,
      changeType: 'address',
      oldJson: employee.address ?? {},
      newJson: {
        address,
      },
    });
  };

  const handlePtoSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (ptoSubmitDisabled) {
      return;
    }
    ptoMutation.mutate({
      policyId: ptoForm.policyId,
      hours: Number.isNaN(ptoHoursValue) ? 0 : ptoHoursValue,
      startDate: ptoForm.startDate,
      endDate: ptoForm.endDate ? ptoForm.endDate : undefined,
      notes: ptoForm.notes ? ptoForm.notes : undefined,
    });
  };

  const handlePolicyAck = () => {
    if (ackSubmitDisabled) {
      return;
    }
    policyAckMutation.mutate({
      policyKey: ackForm.policyKey,
      policyTitle: ackForm.policyTitle,
      version: ackForm.version,
    });
  };

  return (
    <div className="space-y-8">
      <Alert
        variant="default"
        className="border border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100"
      >
        <AlertDescription>
          {t('payrollBridge:banner.employee', {
            defaultValue:
              'Payroll Bridge is read-only for payroll execution. Keep your payroll administrator informed of any changes you submit here.',
          })}
        </AlertDescription>
      </Alert>

      {employee ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle>
                {t('payrollBridge:portal.welcome', {
                  name: employee.legalName,
                  defaultValue: `Welcome, ${employee.legalName}`,
                })}
              </CardTitle>
              <CardDescription>
                {t('payrollBridge:portal.subtitle', {
                  defaultValue: 'Review your onboarding status and submit updates for approval.',
                })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 lg:grid-cols-2">
                <div className="rounded-lg border border-border bg-muted/40 p-4 dark:border-border/60 dark:bg-muted/15">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-foreground">
                      {t('payrollBridge:portal.checklist.title', { defaultValue: 'Onboarding checklist' })}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t('payrollBridge:portal.checklist.progress', {
                        complete: completedTasks.length,
                        total: totalTasks,
                        defaultValue: `${completedTasks.length} of ${totalTasks} complete`,
                      })}
                    </span>
                  </div>
                  <div className="mt-4 flex flex-col gap-2">
                    {tasks.map((task) => (
                      <div
                        key={task.id}
                        className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card px-3 py-2 dark:border-slate-800/70 dark:bg-slate-900/60"
                      >
                        <span className="text-sm font-medium text-foreground">{task.taskType.toUpperCase()}</span>
                        <Badge
                          variant="secondary"
                          className={cn(
                            'border px-2 py-0.5 text-xs font-medium',
                            statusClassName(task.status),
                          )}
                        >
                          {renderTaskStatus(task.status)}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {renderAssignment(task.assignedTo)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg border border-border bg-muted/40 p-4 dark:border-border/60 dark:bg-muted/15">
                  <span className="text-sm font-semibold text-foreground">
                    {t('payrollBridge:portal.contact', { defaultValue: 'Contact information' })}
                  </span>
                  <div className="mt-3 space-y-2 text-sm text-muted-foreground">
                    <div>
                      <span className="font-medium">Email:</span>{' '}
                      <span className="text-muted-foreground">{employee.email}</span>
                    </div>
                    <div>
                      <span className="font-medium">Phone:</span>{' '}
                      <span className="text-muted-foreground">{employee.phone ?? '-'}</span>
                    </div>
                  </div>
                  <div className="mt-4">
                    <span className="text-sm font-semibold text-foreground">
                      {t('payrollBridge:portal.address', { defaultValue: 'Address' })}
                    </span>
                    <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                      <div>
                        {(employee.address?.line1 as string) ??
                          t('payrollBridge:portal.addressPending', {
                            defaultValue: 'Address pending',
                          })}
                      </div>
                      {employee.address?.line2 ? <div>{employee.address.line2 as string}</div> : null}
                      <div>
                        {(employee.address?.city as string) ?? ''}{' '}
                        {(employee.address?.state as string) ?? ''}{' '}
                        {(employee.address?.postalCode as string) ?? ''}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                {t('payrollBridge:portal.changeRequest.title', {
                  defaultValue: 'Request address update',
                })}
              </CardTitle>
              <CardDescription>
                {t('payrollBridge:portal.changeRequest.description', {
                  defaultValue: 'Changes will be reviewed by your manager before they take effect.',
                })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={handleSubmit} className="grid gap-4 lg:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="address-line1">
                    {t('payrollBridge:portal.changeRequest.line1', { defaultValue: 'Address line 1' })}
                  </Label>
                  <Input
                    id="address-line1"
                    value={address.line1}
                    onChange={(event) => setAddress((prev) => ({ ...prev, line1: event.target.value }))}
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="address-line2">
                    {t('payrollBridge:portal.changeRequest.line2', { defaultValue: 'Address line 2' })}
                  </Label>
                  <Input
                    id="address-line2"
                    value={address.line2}
                    onChange={(event) => setAddress((prev) => ({ ...prev, line2: event.target.value }))}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="address-city">
                    {t('payrollBridge:portal.changeRequest.city', { defaultValue: 'City' })}
                  </Label>
                  <Input
                    id="address-city"
                    value={address.city}
                    onChange={(event) => setAddress((prev) => ({ ...prev, city: event.target.value }))}
                    required
                  />
                </div>
                <div className="grid gap-2 lg:grid-cols-2 lg:gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="address-state">
                      {t('payrollBridge:portal.changeRequest.state', { defaultValue: 'State' })}
                    </Label>
                    <Input
                      id="address-state"
                      value={address.state}
                      onChange={(event) =>
                        setAddress((prev) => ({ ...prev, state: event.target.value.toUpperCase() }))
                      }
                      maxLength={2}
                      className="uppercase"
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="address-postal">
                      {t('payrollBridge:portal.changeRequest.postalCode', {
                        defaultValue: 'Postal code',
                      })}
                    </Label>
                    <Input
                      id="address-postal"
                      value={address.postalCode}
                      onChange={(event) =>
                        setAddress((prev) => ({ ...prev, postalCode: event.target.value }))
                      }
                      required
                    />
                  </div>
                </div>
                <div className="lg:col-span-2 flex justify-end">
                  <Button type="submit" disabled={changesetMutation.isLoading}>
                    {changesetMutation.isLoading
                      ? t('payrollBridge:portal.changeRequest.submitting', { defaultValue: 'Submitting…' })
                      : t('payrollBridge:portal.changeRequest.submit', { defaultValue: 'Submit change request' })}
                  </Button>
                </div>
              </form>
              {changesetMutation.isSuccess && submitted ? (
                <Alert className="border border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100">
                  <AlertDescription>
                    {t('payrollBridge:portal.changeRequest.success', {
                      defaultValue: 'Change request submitted for approval.',
                    })}
                  </AlertDescription>
                </Alert>
              ) : null}
            </CardContent>
          </Card>

          <div className="grid gap-6">
            {dashboardQuery.isLoading ? (
              <Card className="border border-border shadow-sm dark:border-slate-800/70">
                <CardHeader>
                  <CardTitle className="text-base font-semibold text-muted-foreground">
                    {t('payrollBridge:ess.title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-1/3" />
                </CardContent>
              </Card>
            ) : null}

            {dashboard ? (
              <>
                <div className="grid gap-6 lg:grid-cols-2">
                  <Card className="border border-border shadow-sm dark:border-slate-800/70">
                    <CardHeader>
                      <CardTitle className="text-base font-semibold text-foreground">
                        {t('payrollBridge:ess.upcomingShifts')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {upcomingShifts.length > 0 ? (
                        <ul className="space-y-3">
                          {upcomingShifts.map((shift) => {
                            const startLabel = shift.startAt
                              ? shiftFormatter.format(new Date(shift.startAt))
                              : '-';
                            const endLabel = shift.endAt
                              ? timeFormatter.format(new Date(shift.endAt))
                              : '-';
                            return (
                              <li
                                key={shift.id}
                                className="rounded-lg border border-border bg-card px-3 py-2 shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60"
                              >
                                <p className="text-sm font-semibold text-foreground">{startLabel}</p>
                                <p className="text-xs text-muted-foreground">
                                  {endLabel} · {shift.locationName ?? '-'}
                                </p>
                                {shift.role ? (
                                  <Badge
                                    variant="outline"
                                    className="mt-2 border-border/60 text-muted-foreground dark:border-border/40"
                                  >
                                    {shift.role}
                                  </Badge>
                                ) : null}
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {t('payrollBridge:ess.noShifts')}
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="border border-border shadow-sm dark:border-slate-800/70">
                    <CardHeader>
                      <CardTitle className="text-base font-semibold text-foreground">
                        {t('payrollBridge:ess.hoursAndTips')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {periodSummaries.length > 0 ? (
                        <div className="space-y-3">
                          {periodSummaries.slice(0, 4).map((period) => {
                            const startLabel = period.startDate
                              ? dateFormatter.format(new Date(period.startDate))
                              : '-';
                            const endLabel = period.endDate
                              ? dateFormatter.format(new Date(period.endDate))
                              : '-';
                            const totalHours =
                              period.regularHours +
                              period.overtimeHours +
                              period.premiumHours +
                              period.ptoHours;
                            const poolTotals = period.tipDistributions.reduce<Map<string, number>>(
                              (acc, distribution) => {
                                const current = acc.get(distribution.poolName) ?? 0;
                                acc.set(distribution.poolName, current + distribution.amountCents);
                                return acc;
                              },
                              new Map(),
                            );
                            const poolPreview = Array.from(poolTotals.entries())
                              .map(([poolName, amountCents]) => ({
                                poolName,
                                amountCents,
                              }))
                              .sort((a, b) => b.amountCents - a.amountCents);
                            const poolCount = poolPreview.length;
                            return (
                              <div
                                key={period.periodId}
                                className="rounded-lg border border-border bg-card px-3 py-2 shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-semibold text-foreground">
                                    {startLabel} – {endLabel}
                                  </span>
                                  {period.complianceFlags.length > 0 ? (
                                    <Badge
                                      variant="outline"
                                      className="border-amber-200 text-amber-900 dark:border-amber-500/40 dark:text-amber-100"
                                    >
                                      {period.complianceFlags.length}
                                    </Badge>
                                  ) : null}
                                </div>
                                <p className="mt-2 text-xs text-muted-foreground">
                                  {t('payrollBridge:ess.hoursSummary', {
                                    hours: totalHours.toFixed(2),
                                    tips: currencyFormatter.format(period.tipsCents / 100),
                                  })}
                                </p>
                                <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
                                  <span>
                                    {t('payrollBridge:owner.overtimeShare')}: {period.overtimeHours.toFixed(2)} hrs
                                  </span>
                                  {period.ptoHours > 0 ? (
                                    <span>PTO: {period.ptoHours.toFixed(2)} hrs</span>
                                  ) : null}
                                  {poolCount > 0 ? (
                                    <span>
                                      {t('payrollBridge:ess.tipPoolCount', {
                                        defaultValue: 'Tip pools: {{count}}',
                                        count: poolCount,
                                      })}
                                    </span>
                                  ) : null}
                                </div>
                                {poolCount > 0 ? (
                                  <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                                    {poolPreview.slice(0, 3).map((pool) => (
                                      <div key={pool.poolName} className="flex items-center justify-between">
                                        <span>{pool.poolName}</span>
                                        <span>{currencyFormatter.format(pool.amountCents / 100)}</span>
                                      </div>
                                    ))}
                                    {poolCount > 3 ? (
                                      <p className="text-right">
                                        {t('payrollBridge:ess.tipPoolPreviewMore', {
                                          defaultValue: '+{{count}} more',
                                          count: poolCount - 3,
                                        })}
                                      </p>
                                    ) : null}
                                  </div>
                                ) : null}
                                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                                  <span>
                                    {t('payrollBridge:ess.tipsByType.cash', {
                                      defaultValue: 'Cash: {{amount}}',
                                      amount: currencyFormatter.format(period.tipsByType.cash / 100),
                                    })}
                                  </span>
                                  <span>
                                    {t('payrollBridge:ess.tipsByType.charged', {
                                      defaultValue: 'Charged: {{amount}}',
                                      amount: currencyFormatter.format(period.tipsByType.charged / 100),
                                    })}
                                  </span>
                                  <span>
                                    {t('payrollBridge:ess.tipsByType.service', {
                                      defaultValue: 'Service: {{amount}}',
                                      amount: currencyFormatter.format(period.tipsByType.serviceCharge / 100),
                                    })}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {t('payrollBridge:timekeeping.noPeriods', {
                            defaultValue: 'No payroll periods staged yet.',
                          })}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                <div className="grid gap-6 lg:grid-cols-2">
                  <Card className="border border-border shadow-sm dark:border-slate-800/70">
                    <CardHeader>
                      <CardTitle className="text-base font-semibold text-foreground">
                        {t('payrollBridge:ess.accrualBalances')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {accrualBalances.length > 0 ? (
                        <div className="space-y-3">
                          {accrualBalances.map((balance) => (
                            <div
                              key={balance.policyId}
                              className="rounded-lg border border-border bg-card px-3 py-2 shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold text-foreground">
                                  {balance.policyName}
                                </span>
                                <Badge
                                  variant="outline"
                                  className="border-emerald-200 text-emerald-900 dark:border-emerald-500/40 dark:text-emerald-100"
                                >
                                  {t('payrollBridge:ess.balanceHours', {
                                    hours: balance.balanceHours.toFixed(2),
                                  })}
                                </Badge>
                              </div>
                              <p className="mt-2 text-xs text-muted-foreground">
                                {t('payrollBridge:ess.accrualRate', {
                                  hours: balance.accrualRatePerHour.toFixed(2),
                                })}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {t('payrollBridge:ess.noBalances')}
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="border border-border shadow-sm dark:border-slate-800/70">
                    <CardHeader>
                      <CardTitle className="text-base font-semibold text-foreground">
                        {t('payrollBridge:ess.ptoRequests')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <form onSubmit={handlePtoSubmit} className="space-y-3">
                        <div className="grid gap-3 lg:grid-cols-2">
                          <div className="grid gap-2">
                            <Label htmlFor="pto-policy">{t('payrollBridge:ess.policyLabel')}</Label>
                            <Select
                              value={ptoForm.policyId}
                              onValueChange={(value) => setPtoForm((prev) => ({ ...prev, policyId: value }))}
                              disabled={accrualBalances.length === 0}
                            >
                              <SelectTrigger id="pto-policy">
                                <SelectValue placeholder={t('payrollBridge:ess.policyLabel')} />
                              </SelectTrigger>
                              <SelectContent>
                                {accrualBalances.map((balance) => (
                                  <SelectItem key={balance.policyId} value={balance.policyId}>
                                    {balance.policyName}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="pto-hours">{t('payrollBridge:ess.hoursLabel')}</Label>
                            <Input
                              id="pto-hours"
                              type="number"
                              step="0.5"
                              min="0"
                              value={ptoForm.hours}
                              onChange={(event) => setPtoForm((prev) => ({ ...prev, hours: event.target.value }))}
                              required
                            />
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="pto-start">{t('payrollBridge:ess.startDate')}</Label>
                            <Input
                              id="pto-start"
                              type="date"
                              value={ptoForm.startDate}
                              onChange={(event) => setPtoForm((prev) => ({ ...prev, startDate: event.target.value }))}
                              required
                            />
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="pto-end">{t('payrollBridge:ess.endDate')}</Label>
                            <Input
                              id="pto-end"
                              type="date"
                              value={ptoForm.endDate}
                              onChange={(event) => setPtoForm((prev) => ({ ...prev, endDate: event.target.value }))}
                            />
                          </div>
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="pto-notes">{t('payrollBridge:ess.notesLabel')}</Label>
                          <Textarea
                            id="pto-notes"
                            value={ptoForm.notes}
                            onChange={(event) => setPtoForm((prev) => ({ ...prev, notes: event.target.value }))}
                            rows={3}
                          />
                        </div>
                        <div className="flex justify-end">
                          <Button type="submit" disabled={ptoSubmitDisabled || ptoMutation.isLoading}>
                            {ptoMutation.isLoading
                              ? t('payrollBridge:ess.requestPtoSubmitting')
                              : t('payrollBridge:ess.requestPto')}
                          </Button>
                        </div>
                      </form>
                      <div className="space-y-3">
                        {ptoRequests.length > 0 ? (
                          ptoRequests.map((request) => {
                            const startLabel = request.startDate
                              ? dateFormatter.format(new Date(request.startDate))
                              : '-';
                            const endLabel = request.endDate
                              ? dateFormatter.format(new Date(request.endDate))
                              : null;
                            return (
                              <div
                                key={request.id}
                                className="rounded-lg border border-border bg-card px-3 py-2 shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-semibold text-foreground">
                                    {startLabel}
                                    {endLabel ? ` – ${endLabel}` : ''}
                                  </span>
                                  <Badge
                                    variant="outline"
                                    className={cn(
                                      'border px-2 py-0.5 text-xs font-medium',
                                      PTO_STATUS_STYLES[request.status],
                                    )}
                                  >
                                    {t(`payrollBridge:ess.${request.status}Status`)}
                                  </Badge>
                                </div>
                                <p className="mt-2 text-xs text-muted-foreground">
                                  {t('payrollBridge:owner.hoursValue', {
                                    hours: request.hours.toFixed(2),
                                  })}
                                </p>
                                {request.notes ? (
                                  <p className="mt-2 text-xs text-muted-foreground">{request.notes}</p>
                                ) : null}
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            {t('payrollBridge:ess.noRequests')}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid gap-6 lg:grid-cols-2">
                  <Card className="border border-border shadow-sm dark:border-slate-800/70">
                    <CardHeader>
                      <CardTitle className="text-base font-semibold text-foreground">
                        {t('payrollBridge:ess.documents')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {documents.length > 0 ? (
                        documents.map((doc) => {
                          const meta = doc.metadata ?? {};
                          const signedLabel = doc.signedAt ? shiftFormatter.format(new Date(doc.signedAt)) : null;
                          return (
                            <div
                              key={doc.id}
                              className="rounded-lg border border-border bg-card px-3 py-2 shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold text-foreground">{doc.docType.toUpperCase()}</span>
                                {signedLabel ? (
                                  <span className="text-xs text-muted-foreground">{signedLabel}</span>
                                ) : null}
                              </div>
                              {typeof meta.version === 'string' ? (
                                <p className="mt-1 text-xs text-muted-foreground">Version {String(meta.version)}</p>
                              ) : null}
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {t('payrollBridge:ess.noDocuments')}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                        <Input
                          placeholder={t('payrollBridge:ess.ackPolicyKeyPlaceholder')}
                          value={ackForm.policyKey}
                          onChange={(event) =>
                            setAckForm((prev) => ({ ...prev, policyKey: event.target.value }))
                          }
                        />
                        <Input
                          placeholder={t('payrollBridge:ess.ackPolicyTitlePlaceholder')}
                          value={ackForm.policyTitle}
                          onChange={(event) =>
                            setAckForm((prev) => ({ ...prev, policyTitle: event.target.value }))
                          }
                        />
                        <Input
                          placeholder={t('payrollBridge:ess.ackVersionPlaceholder')}
                          value={ackForm.version}
                          onChange={(event) =>
                            setAckForm((prev) => ({ ...prev, version: event.target.value }))
                          }
                        />
                        <Button
                          type="button"
                          onClick={handlePolicyAck}
                          disabled={ackSubmitDisabled || policyAckMutation.isLoading}
                        >
                          {policyAckMutation.isLoading
                            ? t('payrollBridge:ess.policyAckSubmitting')
                            : t('payrollBridge:ess.policyAck')}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border border-border shadow-sm dark:border-slate-800/70">
                    <CardHeader>
                      <CardTitle className="text-base font-semibold text-foreground">
                        {t('payrollBridge:ess.alertsTitle')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {essAlerts.length > 0 ? (
                        <div className="space-y-3">
                          {essAlerts.map((alert) => (
                            <div
                              key={alert.id}
                              className={cn('rounded-lg border px-3 py-2 shadow-sm', ESS_ALERT_STYLES[alert.severity])}
                            >
                              <p className="text-sm font-semibold">{alert.title}</p>
                              <p className="mt-1 text-xs text-muted-foreground">{alert.message}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {t('payrollBridge:ess.alertsEmpty')}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </>
            ) : null}
          </div>
        </>
      ) : employeeQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">
          {t('payrollBridge:portal.loading', { defaultValue: 'Loading profile…' })}
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          {t('payrollBridge:portal.notFound', { defaultValue: 'Employee record not found.' })}
        </p>
      )}
    </div>
  );
};

export default PayrollEmployeePortal;
